import { useState } from "react";
import { Sparkles, Compass, CheckCircle, Download, Edit3, Save, TrendingUp, Eye, Target, Zap } from "lucide-react";
import { Button } from "@/components/ui/button";

const defaultContent = {
  brandName: "Thrive with Deepa",
  hookLine: "Are You Really Thriving in Your Career?",
  badge: "Free Self-Assessment · Instant Insights",
  title: "Discover Your",
  titleHighlight: "Career Blind Spots",
  subtitle: "The Career Wheel — A Visual Snapshot of Your Career Satisfaction",
  intriguingHook: "Most people don't realize what's holding them back until they see it visualized. This 5-minute assessment reveals exactly where to focus.",
  dimensions: [
    "Passion & Enthusiasm",
    "Challenge & Growth",
    "Flexibility & Autonomy",
    "Salary & Benefits",
    "Career Progression",
    "Work/Life Balance",
    "Skill Development",
    "Meaning & Purpose",
    "Personal Growth",
  ],
  benefits: [
    "🎯 Rate 9 critical career dimensions",
    "📊 Get an instant visual radar chart",
    "🔍 Discover your hidden blind spots",
    "💡 Receive personalized insights from Deepa",
    "⏱️ Takes only 5 minutes to complete",
  ],
  accessNote: "🔐 Exclusive Access · Code Required",
  urgency: "✨ Free for a limited time",
  cta: "🚀 Get Your Access Code",
  link: "deepa-coaching.com/#career-wheel",
  credentials: "Deepa Prashanth — Leadership & Career Coach | 275+ Coaching Hours | ICF Level 1 Trained | PROSCI Certified",
  contact: "bookings@deepa-coaching.com",
};

const CareerWheelPoster = () => {
  const [content, setContent] = useState(defaultContent);
  const [isEditing, setIsEditing] = useState(false);

  const handleDownload = () => window.print();
  const toggleEdit = () => setIsEditing(!isEditing);

  const updateField = (field, value) => {
    setContent((prev) => ({ ...prev, [field]: value }));
  };

  const updateArrayItem = (arrayKey, index, value) => {
    setContent((prev) => ({
      ...prev,
      [arrayKey]: prev[arrayKey].map((b, i) => (i === index ? value : b)),
    }));
  };

  const EditableText = ({ field, className, tag = "span", style = {} }) => {
    const Tag = tag;
    if (isEditing) {
      return (
        <Tag
          contentEditable
          suppressContentEditableWarning
          onBlur={(e) => updateField(field, e.target.textContent)}
          className={`${className} outline-2 outline-dashed outline-primary/50 outline-offset-2 focus:outline-primary`}
          style={style}
        >
          {content[field]}
        </Tag>
      );
    }
    return <Tag className={className} style={style}>{content[field]}</Tag>;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-accent/5 to-orange-50 py-8 px-4 print:p-0 print:bg-white">
      {/* Action Buttons */}
      <div className="max-w-2xl mx-auto mb-6 flex flex-wrap justify-center gap-3 print:hidden">
        <Button
          onClick={toggleEdit}
          data-testid="edit-poster-btn"
          className={isEditing ? "bg-green-600 hover:bg-green-700 text-white" : "bg-primary text-white hover:bg-primary/90"}
        >
          {isEditing ? <><Save className="mr-2 h-4 w-4" />Save Changes</> : <><Edit3 className="mr-2 h-4 w-4" />Edit Poster</>}
        </Button>
        <Button onClick={handleDownload} className="bg-primary text-white hover:bg-primary/90">
          <Download className="mr-2 h-4 w-4" />Print / Save as PDF
        </Button>
        <Button onClick={() => (window.location.href = "/")} variant="outline">Back to Home</Button>
      </div>

      {isEditing && (
        <div className="max-w-2xl mx-auto mb-4 p-3 bg-yellow-50 border border-yellow-300 rounded-lg text-center text-sm print:hidden">
          ✏️ <strong>Editing Mode:</strong> Click any text to edit it. Click "Save Changes" when done.
        </div>
      )}

      {/* Poster */}
      <div className="max-w-2xl mx-auto bg-white rounded-3xl shadow-2xl overflow-hidden print:shadow-none print:rounded-none">
        {/* Hook Header */}
        <div 
          className="relative h-52 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1631712734850-1d1ecf33fe4e?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1200')" }}
        >
          <div className="absolute inset-0 bg-black/50" />
          <div className="absolute inset-0 flex flex-col items-center justify-center px-6 text-center">
            <div className="mb-2">
              <Compass className="h-10 w-10 text-white mx-auto mb-2" style={{ filter: "drop-shadow(2px 2px 4px rgba(0,0,0,0.5))" }} />
            </div>
            <EditableText
              field="hookLine"
              tag="h1"
              className="text-3xl md:text-4xl font-bold text-white"
              style={{ textShadow: "2px 2px 8px rgba(0,0,0,0.6)" }}
            />
          </div>
        </div>

        <div className="p-8 md:p-10">
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-primary/10 border-2 border-primary/30 rounded-full">
              <TrendingUp className="h-4 w-4 text-primary" />
              <EditableText field="badge" className="text-xs font-bold uppercase tracking-wider text-primary" />
            </div>
          </div>

          {/* Main Title */}
          <div className="text-center mb-6">
            <EditableText field="title" tag="h2" className="text-3xl md:text-5xl font-bold tracking-tight mb-1 block" />
            <EditableText field="titleHighlight" tag="h2" className="text-3xl md:text-5xl font-bold tracking-tight text-primary block" />
            <EditableText field="subtitle" tag="p" className="text-sm md:text-base text-muted-foreground italic mt-3" />
          </div>

          {/* Intriguing Hook */}
          <div className="bg-yellow-50 border-2 border-yellow-300 rounded-2xl p-5 mb-6">
            <div className="flex items-start gap-3">
              <Eye className="h-5 w-5 text-yellow-700 mt-0.5 flex-shrink-0" />
              <EditableText field="intriguingHook" tag="p" className="text-sm text-yellow-900 leading-relaxed" />
            </div>
          </div>

          {/* Dimensions Preview */}
          <div className="mb-6">
            <h3 className="text-base font-bold mb-3 text-center flex items-center justify-center gap-2">
              <Target className="h-4 w-4 text-primary" />
              You'll Assess These 9 Dimensions:
            </h3>
            <div className="grid grid-cols-3 gap-2">
              {content.dimensions.map((dim, i) => (
                <div key={i} className="text-center p-2 bg-primary/5 rounded-lg border border-primary/10">
                  {isEditing ? (
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateArrayItem("dimensions", i, e.target.textContent)}
                      className="text-xs font-medium outline-2 outline-dashed outline-primary/50"
                    >{dim}</span>
                  ) : (
                    <span className="text-xs font-medium">{dim}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Benefits */}
          <div className="bg-gradient-to-br from-primary/5 to-accent/5 rounded-2xl p-6 mb-6">
            <h3 className="text-base font-bold mb-4 text-center">What You'll Get:</h3>
            <div className="space-y-2">
              {content.benefits.map((benefit, i) => (
                <div key={i} className="flex items-start gap-2">
                  {isEditing ? (
                    <span
                      contentEditable
                      suppressContentEditableWarning
                      onBlur={(e) => updateArrayItem("benefits", i, e.target.textContent)}
                      className="text-sm outline-2 outline-dashed outline-primary/50 outline-offset-2"
                    >{benefit}</span>
                  ) : (
                    <span className="text-sm">{benefit}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Access Notice */}
          <div className="bg-primary/10 border-2 border-primary/30 rounded-xl p-4 mb-6 text-center">
            <EditableText field="accessNote" tag="p" className="text-sm font-semibold text-primary" />
          </div>

          {/* Urgency */}
          <div className="text-center mb-4">
            <EditableText field="urgency" className="text-sm font-semibold text-orange-600" />
          </div>

          {/* CTA */}
          <div className="text-center">
            <div className="inline-block bg-primary text-white px-8 py-4 rounded-full font-bold text-lg mb-3 shadow-lg">
              <EditableText field="cta" />
            </div>
            <p className="text-sm text-muted-foreground mb-1">Request Access at:</p>
            <a 
              href={`https://${content.link}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-base font-bold text-primary hover:underline break-all"
            >{content.link}</a>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-border/50 p-4 bg-primary/5">
          <EditableText field="credentials" tag="p" className="text-xs text-center text-muted-foreground leading-relaxed" />
          <p className="text-center text-sm font-semibold text-primary mt-2">
            📧 <EditableText field="contact" />
          </p>
        </div>

        <div className="bg-primary py-3 text-center">
          <p className="text-2xl font-bold text-white" style={{ fontFamily: "'Dancing Script', cursive" }}>
            {content.brandName}
          </p>
        </div>
      </div>

      <div className="max-w-2xl mx-auto mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm print:hidden">
        <h4 className="font-bold mb-2">📱 How to Share:</h4>
        <ul className="list-disc list-inside space-y-1 text-muted-foreground">
          <li>Click <strong>Edit Poster</strong> to customize any text</li>
          <li>Click <strong>Print / Save as PDF</strong> to download</li>
          <li>Screenshot on phone to share on WhatsApp/Instagram</li>
        </ul>
      </div>

      <style>{`@media print { body { background: white !important; } @page { margin: 0; size: A4; } }`}</style>
    </div>
  );
};

export default CareerWheelPoster;
